/**
 * Legality for the **self-directed** verbs — Full Tech's opener/continuation, Stabilize, Boot
 * Up, Self-Destruct, Overcharge, and COMP/CON Engage.
 */

import type { EncounterState } from "@lancer-vtt/domain";
import { COMPCON_ENGAGED_STATUS, REPAIR_CAP_RESOURCE, SHUT_DOWN_STATUS, TG_AI, TG_NO_CASCADE, UNPILOTED_STATUS, canUseAction, hasStatus, onBracedTurn } from "@lancer-vtt/domain";
import type { RequestBootUp, RequestEngageCompCon, RequestTakeBackCompCon, RequestOvercharge, RequestSelfDestruct, RequestStabilize } from "@lancer-vtt/shared";
import { ok, err } from "@lancer-vtt/shared";
import { quickTechOption } from "../quickTech";
import type { LoadedOverlay } from "../effects";
import { isQuickTechAction } from "./actionShapes";
import { isActivateActionLegal } from "./carriedActions";
import { isTurnVirgin } from "./ordnance";
import type { BudgetVerdict } from "./types";
import { affordable } from "../events/gameEvents";

/** The actor and overlay a Full Tech pick naming a carried Quick Tech action is checked against. */
export type FullTechCarrier = { state: EncounterState; actor: string; overlay: LoadedOverlay };

/**
 * May a Full Tech **open** with `option` (the opener's structural wall, run before any dice)? The
 * option must be a known Quick Tech id (`scan` | `lock_on` | `invade` | `bolster`), or, given
 * `carrier`, a Quick Tech action (`activation: "quick_tech"`) the actor carries (Crush Targeting).
 */
export function checkFullTechOpener(option: string, carrier?: FullTechCarrier): BudgetVerdict {
  if (!quickTechOption(option) && !(carrier !== undefined && isCarriedQuickTech(carrier, option))) {
    return err(`full tech: unknown Quick Tech option "${option}"`);
  }
  return ok(undefined);
}

/**
 * May a Full Tech **continue** with `option`, having already fired `fired` this Full Tech? The
 * opener's structural wall, plus one rule for NPCs. A mech may repeat an option (Core p.71:
 * Bolster + Bolster), so it has no "already fired" wall, unlike Barrage. An NPC's Full Tech is
 * "two different Quick Tech actions" (Core p.280), so an NPC repeating its first pick is refused.
 */
export function checkFullTechContinuation(option: string, fired: readonly string[], carrier: FullTechCarrier): BudgetVerdict {
  const structural = checkFullTechOpener(option, carrier);
  if (!structural.ok) return structural;
  const actor = carrier.state.combatants[carrier.actor];
  if (actor?.kind === "npc" && fired.includes(option)) {
    const label = quickTechOption(option)?.name ?? "that Quick Tech";
    return err(`full tech: ${actor.name} already used ${label} this Full Tech, and an NPC's two Quick Techs must be different`);
  }
  return ok(undefined);
}

/** Does `carrier.actor` carry `option` as a Quick Tech action (see {@link checkFullTechOpener})? */
export function isCarriedQuickTech(carrier: FullTechCarrier, option: string): boolean {
  const { state, actor, overlay } = carrier;
  return isQuickTechAction(actor, option, overlay) && isActivateActionLegal({ state, actorId: actor, queriedId: option, overlay });
}

/**
 * May `intent.actor` take this **Stabilize**? The one gameplay-tier wall: the **Patch** primary
 * spends one repair from the actor's Repair Cap (the `repair_cap` resource), so a Patch with **no
 * repairs remaining** is **hard-rejected** (RAW: you cannot spend a repair you don't have). A
 * hard reject in the rules-following tier — there is no `force` flag; a GM who must heal an
 * out-of-repairs mech reaches for a separate force-apply (`set_hp`), not a softened Stabilize.
 * The **Cool** primary has no resource cost, so it is always legal.
 *
 * Returns a verdict the room surfaces (a rejection is a logged no-op before the resolution).
 */
export function checkStabilizeLegality(state: EncounterState, intent: RequestStabilize): BudgetVerdict {
  const actor = state.combatants[intent.actor];
  if (!actor) return err(`stabilize: ${intent.actor} is not on the map`);
  if (intent.primary === "patch") {
    const remaining = actor.current.resources?.[REPAIR_CAP_RESOURCE] ?? 0;
    if (remaining < 1) {
      return err(`stabilize: ${actor.name} has no repairs remaining (Repair Cap exhausted) — cannot Patch`);
    }
  }
  return ok(undefined);
}

/**
 * May `intent.actor` **Boot Up** (Core Book p.72)? The
 * one gameplay-tier wall: Boot Up's entire effect is removing the **Shut Down** status, so an
 * actor that isn't currently Shut Down has nothing to boot up — hard-rejected (there is no
 * "no-op success" reading; unlike Disengage/Hide's idempotent-already-applied shape, taking a
 * Full action that provably does nothing is a genuine illegal call, not a quiet no-op). Shut
 * Down itself carries no precondition (RAW attaches none — it is always legal to take).
 *
 * Returns a verdict the room surfaces (a rejection is a logged no-op before the resolution).
 */
export function checkBootUpLegality(state: EncounterState, intent: RequestBootUp): BudgetVerdict {
  const actor = state.combatants[intent.actor];
  if (!actor) return err(`boot up: ${intent.actor} is not on the map`);
  if (!hasStatus(actor.statuses, SHUT_DOWN_STATUS)) {
    return err(`boot up: ${actor.name} is not Shut Down — nothing to boot up`);
  }
  return ok(undefined);
}

/**
 * May `intent.actor` **Self-Destruct** (Core Book pp.71-72)? The one gameplay-tier wall:
 * `intent.countdown` (the RAW "your choice" of detonation
 * timing) must be an integer in **1..3** — RAW: "at the end of your next turn [1], or at
 * the end of one of your turns within the following two rounds [2 or 3] (your choice)".
 * There is no other precondition (RAW attaches none to arming — always legal, and
 * re-issuing legally re-arms rather than stacking).
 *
 * Returns a verdict the room surfaces (a rejection is a logged no-op before the resolution).
 */
export function checkSelfDestructLegality(state: EncounterState, intent: RequestSelfDestruct): BudgetVerdict {
  const actor = state.combatants[intent.actor];
  if (!actor) return err(`self-destruct: ${intent.actor} is not on the map`);
  if (!Number.isInteger(intent.countdown) || intent.countdown < 1 || intent.countdown > 3) {
    return err(`self-destruct: --at must be an integer 1-3 (got ${intent.countdown})`);
  }
  return ok(undefined);
}

/**
 * May `intent.actor` **Overcharge**? The gameplay-tier walls, all run before any dice (RAW Core
 * Book p.78; NPC block p.280):
 *   - **mech-only** — only a `kind: "mech"` combatant may Overcharge; an NPC / pilot / object /
 *     deployable is hard-rejected (the NPC action catalog must never surface Overcharge);
 *   - **on its own turn** — the actor must be the unit mid-turn (`turn.phase === "acting"` and
 *     `turn.actor === actor`): you push your reactor on your turn, not out of turn;
 *   - **once per turn** — RAW is once per *turn* (not per round): a second Overcharge in the same
 *     turn is rejected via {@link canUseAction} (`@lancer-vtt/domain`) reading
 *     `turnBudget.actionUses["overcharge"]` (set by `grant_overcharge`, reset with the rest of the
 *     ledger each `activate`).
 *
 * A hard reject in the rules-following tier — there is no `force` flag. Returns a verdict the room
 * surfaces (a rejection is a logged no-op).
 */
export function checkOverchargeLegality(state: EncounterState, intent: RequestOvercharge): BudgetVerdict {
  const actor = state.combatants[intent.actor];
  if (!actor) return err(`overcharge: ${intent.actor} is not on the map`);
  if (actor.kind !== "mech") {
    return err(`overcharge: ${actor.name} is a ${actor.kind} — only mechs can Overcharge (NPCs cannot)`);
  }
  const turn = state.activation.turn;
  if (turn.phase !== "acting" || turn.actor !== intent.actor) {
    return err(`overcharge: ${actor.name} can only Overcharge on its own turn`);
  }
  if (onBracedTurn(actor, state.activation)) {
    return err(`overcharge: ${actor.name} Braced last turn and can't Overcharge this turn (RAW p.73)`);
  }
  const allowance = actor.base.actionAllowances?.overcharge ?? 1;
  if (!affordable({ kind: "action_points", carrier: actor.id, points: 0, actionId: "overcharge", allowance }, state, undefined)) {
    return err(`overcharge: ${actor.name} has already Overcharged this turn (once per turn)`);
  }
  return ok(undefined);
}

/** Whether a frozen tag-ref list carries the given tag id (shared shape with `tables.ts`'s `hasTagId`). */
function hasTagId(tags: { id: string; val?: number }[] | undefined, tagId: string): boolean {
  return (tags ?? []).some((t) => t.id === tagId);
}

/**
 * Does `base` carry ≥1 equipped SYSTEM that is a **cascade-immune AI system** — one tagged
 * BOTH {@link TG_AI} AND {@link TG_NO_CASCADE}? Read live off `base.equippedSystems` at check
 * time (re-derived, never a spawn-frozen flag, mirroring `tables.ts`'s `hasActiveCascadeSource`)
 * — Engage COMP/CON's reachability always reflects the CURRENT loadout.
 *
 * **Don't reach for a dedicated COMP/CON-class tag: there isn't one.** No such tag exists in
 * upstream `@massif/lancer-data`, and the core-rulebook pack ships only effect overlays (no
 * base-tag layer), so a REAL comp/con spawn could never carry one and Engage would be reachable
 * only through a synthetic fixture. The `tg_ai` + `tg_no_cascade` PAIR is the marker, verified
 * against upstream shipped data: the real comp/con (`ms_comp_con_class_assistant_unit`) carries
 * `[tg_ai, tg_no_cascade, tg_unique]`; technophile's custom NHP (`ms_technophile_1`) carries
 * `[tg_ai, tg_unique]` — `tg_ai` alone, no `tg_no_cascade` — so it correctly stays NOT
 * Engage-reachable. The only OTHER `tg_ai` + `tg_no_cascade` carrier in all of shipped Lancer
 * data is `wm_uncle_class_comp_con`, itself a COMP/CON-class unit — so the tag pair genuinely IS
 * Massif's comp/con-class marker, not a coincidental proxy.
 *
 * **Exported** so the combat surface's Engage COMP/CON row reads the SAME reachability predicate
 * `checkEngageCompConLegality` gates on, rather than duplicating a tag scan in the frontend
 * (`CLAUDE.md`: "UI mirrors the reducer's rules as affordances; it never re-enforces them").
 */
export function hasCompConSystem(base: EncounterState["combatants"][string]["base"]): boolean {
  return (base.equippedSystems ?? []).some((s) => hasTagId(s.tags, TG_AI) && hasTagId(s.tags, TG_NO_CASCADE));
}

/**
 * May `intent.actor` **Engage COMP/CON** (RAW Core p.71)?
 * Two gameplay-tier walls, checked in order: (1) the actor must carry a cascade-immune AI
 * system ({@link hasCompConSystem} — `tg_ai` + `tg_no_cascade`) — no comp/con-class system,
 * nothing to engage; (2) the actor must NOT already be Engaged — a protocol you've already
 * run is idempotent, not a re-charge
 * (the persistent marker status IS the once-constraint; no separate `protocolUses` ledger
 * entry is needed for this bespoke intent).
 *
 * Returns a verdict the room surfaces (a rejection is a logged no-op before the resolution).
 */
export function checkEngageCompConLegality(state: EncounterState, intent: RequestEngageCompCon): BudgetVerdict {
  const actor = state.combatants[intent.actor];
  if (!actor) return err(`engage compcon: ${intent.actor} is not on the map`);
  if (!hasCompConSystem(actor.base)) {
    return err(`engage compcon: ${actor.name} carries no COMP/CON-class system — nothing to engage`);
  }
  if (hasStatus(actor.statuses, COMPCON_ENGAGED_STATUS)) {
    return err(`engage compcon: ${actor.name} has already Engaged its COMP/CON`);
  }
  return ok(undefined);
}

/**
 * May `intent.actor`'s pilot **take control back** from COMP/CON (Core p.106: "At the start of the
 * player's subsequent turns, control can be returned to the pilot as long as they are physically
 * inside their mech")? In order: the mech is Engaged; it is the mech's own turn and nothing has
 * been spent on it yet (the start-of-turn window every Protocol shares, {@link isTurnVirgin});
 * the turn is a later one than the Engage (`appliedTurn`); and the pilot is aboard (not
 * {@link UNPILOTED_STATUS}).
 */
export function checkTakeBackCompConLegality(state: EncounterState, intent: RequestTakeBackCompCon): BudgetVerdict {
  const actor = state.combatants[intent.actor];
  if (!actor) return err(`take back from compcon: ${intent.actor} is not on the map`);
  const engaged = actor.statuses.find((s) => s.name === COMPCON_ENGAGED_STATUS);
  if (engaged === undefined) return err(`take back from compcon: ${actor.name}'s COMP/CON isn't Engaged`);
  const turn = state.activation.turn;
  if (turn.phase !== "acting" || turn.actor !== actor.id || !isTurnVirgin(state, actor.id)) {
    return err(`take back from compcon: only at the start of ${actor.name}'s own turn`);
  }
  if (engaged.appliedTurn === state.activation.turnOrdinal) {
    return err(`take back from compcon: ${actor.name} Engaged its COMP/CON this turn; take control back at the start of a later turn`);
  }
  if (hasStatus(actor.statuses, UNPILOTED_STATUS)) return err(`take back from compcon: ${actor.name}'s pilot isn't aboard`);
  return ok(undefined);
}
